import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@mlm/ui';
import type { Membership } from '../../../lib/api';

/**
 * Per-tier `unilevel_consecutive_bonus` editor. Each tier stores a top-level
 *   memberships[tier].config.unilevel_consecutive_bonus[months] = amount
 * map (consecutive-qualifying-months → bonus amount). Rows = tier, columns =
 * month buckets (admin adds/removes), cell = amount.
 */
export function UnilevelConsecutivePerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const presentMonths = useMemo(() => {
    const set = new Set<number>();
    for (const cfg of Object.values(draft)) {
      const map = cfg.unilevel_consecutive_bonus as Record<string, unknown> | undefined;
      if (map) for (const k of Object.keys(map)) {
        const n = Number(k);
        if (Number.isFinite(n) && n > 0) set.add(n);
      }
    }
    if (set.size === 0) set.add(3);
    return Array.from(set).sort((a, b) => a - b);
  }, [draft]);

  const [extra, setExtra] = useState<number[]>([]);
  const months = useMemo(() => {
    const set = new Set<number>(presentMonths);
    for (const n of extra) set.add(n);
    return Array.from(set).sort((a, b) => a - b);
  }, [presentMonths, extra]);

  const [newMonth, setNewMonth] = useState('');

  function getCell(cfg: Record<string, unknown>, month: number): number {
    const map = (cfg.unilevel_consecutive_bonus as Record<string, unknown> | undefined) ?? {};
    const v = map[String(month)];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }

  function setCell(id: string, month: number, n: number) {
    const current = draft[id] ?? {};
    const map = { ...((current.unilevel_consecutive_bonus as Record<string, unknown> | undefined) ?? {}) };
    if (n === 0) delete map[String(month)];
    else map[String(month)] = n;
    const next = { ...current };
    if (Object.keys(map).length === 0) delete next.unilevel_consecutive_bonus;
    else next.unilevel_consecutive_bonus = map;
    onChange(id, next);
  }

  function addMonth() {
    const n = Number(newMonth);
    if (!Number.isFinite(n) || n <= 0 || months.includes(n)) return;
    setExtra((prev) => [...prev, n]);
    setNewMonth('');
  }

  function removeMonth(month: number) {
    for (const m of memberships) setCell(m.id, month, 0);
    setExtra((prev) => prev.filter((x) => x !== month));
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-muted-foreground">
        Bonus paid when a slot of the receiver tier stays qualified for N
        consecutive periods. Columns = consecutive-month thresholds; cell =
        cash bonus at that streak.
      </p>

      {!readOnly ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            step={1}
            placeholder="Add month #"
            className="h-8 w-32 px-2 rounded-md border bg-background text-xs"
            value={newMonth}
            onChange={(e) => setNewMonth(e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={addMonth}>
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
      ) : null}

      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">Tier \ Months</th>
              {months.map((mo) => (
                <th key={mo} className="px-3 py-2 font-mono font-medium">
                  <div className="flex items-center gap-1">
                    {mo}mo
                    {!readOnly && months.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeMonth(mo)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${mo} months`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((tier) => (
              <tr key={tier.id} className="border-t">
                <td
                  className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                  title={tier.name}
                >
                  {tier.code}
                </td>
                {months.map((mo) => (
                  <td key={mo} className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getCell(draft[tier.id] ?? {}, mo)}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      onChange={(e) => setCell(tier.id, mo, Number(e.target.value) || 0)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
